import { useCallback } from "react";

const MEETERGO_JS_URL = "https://liv-showcase.s3.eu-central-1.amazonaws.com/browser-v4.js";
const LOAD_TIMEOUT_MS = 5000;
const READY_POLL_INTERVAL_MS = 50;

/** Module-scoped in-flight promise so concurrent calls share one load attempt. */
let loadPromise: Promise<void> | null = null;

function isMeetergoReady(): boolean {
  const api = window.meetergo;
  // browser-v4.js attaches `window.meetergo` as a callable function with
  // `.modal`, `.setPrefill`, etc. bound once the SDK has booted.
  // Check every method the hook actually calls so a partially-initialized
  // SDK can't slip through (e.g. if a future build attaches methods in a
  // different order).
  return (
    typeof api === "function" && typeof api.modal === "function" && typeof api.setPrefill === "function"
  );
}

/**
 * Poll until the meetergo API is callable, or timeout.
 *
 * browser-v4.js attaches `window.meetergo` and initializes asynchronously,
 * so the `load` event firing isn't a reliable readiness signal. We probe
 * for every method `openPopup` calls to confirm the SDK has booted fully.
 *
 * Accepts an optional AbortSignal — aborting stops the polling loop
 * immediately so a failed load attempt doesn't keep the event loop busy
 * until the timeout fires.
 */
function waitForMeetergoReady(timeoutMs: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }

    const start = Date.now();
    let timer: ReturnType<typeof setTimeout> | undefined;

    const onAbort = () => {
      if (timer !== undefined) clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });

    const cleanup = () => {
      signal?.removeEventListener("abort", onAbort);
    };

    const tick = () => {
      if (isMeetergoReady()) {
        cleanup();
        resolve();
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        cleanup();
        reject(new Error("Meetergo script did not become ready in time"));
        return;
      }
      timer = setTimeout(tick, READY_POLL_INTERVAL_MS);
    };

    tick();
  });
}

/**
 * Lazy-inject browser-v4.js and resolve once `window.meetergo` is callable.
 *
 * Deduplicates concurrent callers via a module-level `loadPromise` — repeated
 * clicks while the script is still loading all await the same load attempt.
 * On failure, the cache is cleared so a later click retries.
 */
function ensureMeetergoLoaded(): Promise<void> {
  if (isMeetergoReady()) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    // Reuse any existing tag — the script may have been added by a prior
    // (failed or still-loading) attempt before the cache was cleared.
    const existing = document.querySelector(`script[src="${MEETERGO_JS_URL}"]`) as HTMLScriptElement | null;
    const script =
      existing ??
      (() => {
        const s = document.createElement("script");
        s.src = MEETERGO_JS_URL;
        s.defer = true;
        document.head.appendChild(s);
        return s;
      })();

    let settled = false;
    // Shared controller so `onError` can stop the readiness poll immediately
    // instead of letting its setTimeout chain run until LOAD_TIMEOUT_MS.
    const pollController = new AbortController();

    /**
     * Remove the failed <script> from the DOM so the next attempt can create
     * a fresh one. Browsers don't re-dispatch load/error on an existing
     * already-failed tag — without this, subsequent clicks would reuse the
     * dead tag, attach a listener that never fires, and always time out.
     */
    const discardScriptForRetry = () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };

    const onError = () => {
      if (settled) return;
      settled = true;
      pollController.abort();
      discardScriptForRetry();
      loadPromise = null; // allow a retry on the next click
      reject(new Error("Meetergo script failed to load"));
    };

    // `{ once: true }` auto-removes the listener on fire — combined with the
    // `settled` guard, this prevents leaks and double-settle races even if
    // the readiness poll resolves first.
    script.addEventListener("error", onError, { once: true });

    // Single readiness waiter per load attempt.
    waitForMeetergoReady(LOAD_TIMEOUT_MS, pollController.signal)
      .then(() => {
        if (settled) return;
        settled = true;
        script.removeEventListener("error", onError);
        resolve();
      })
      .catch((err) => {
        // Aborted rejections (from the onError path) are expected — settled
        // is already true and the outer reject has already been called.
        if (settled) return;
        settled = true;
        script.removeEventListener("error", onError);
        // Same rationale as onError: if the script loaded but the SDK never
        // exposed its API within the timeout, leave the tag in place — it
        // may still initialize after the next click, in which case the
        // module-level `isMeetergoReady()` short-circuit will catch it. If
        // it's stuck and the next click hits another timeout, that's the
        // same 5 s cost we'd pay on a fresh injection anyway.
        loadPromise = null; // allow a retry on the next click
        reject(err);
      });
  });

  return loadPromise;
}

/**
 * Provides a function to open a Meetergo booking popup.
 * The script is lazy-loaded on first call — no network cost until the user clicks.
 */
export function useMeetergo() {
  /**
   * Open a Meetergo booking popup for the given URL.
   *
   * @param url     Full booking URL copied from Meetergo's embed modal.
   *                Query params baked into the URL (e.g. `?hide_details=true`)
   *                are preserved — don't append anything here.
   * @param prefill Optional prefill values keyed by the form's data field IDs.
   *                See {@link MeetergoPrefill} for the predefined slugs;
   *                custom advisor-specific slugs are allowed via the index
   *                signature. Keys that don't match a field on the form are
   *                silently ignored, so callers can safely send a superset
   *                to cover forms with different field configurations.
   *
   * On script-load failure the URL is opened in a new tab as a fallback.
   * Because that fallback runs after an `await`, the browser may block it
   * as a non-user-gesture popup.
   */
  const openPopup = useCallback(async (url: string, prefill?: MeetergoPrefill) => {
    try {
      await ensureMeetergoLoaded();
      const api = window.meetergo!;
      // Apply prefill via the documented setPrefill API; this merges onto the
      // SDK's internal config so the booking form's fields populate.
      if (prefill) api.setPrefill(prefill);
      // Open the booking modal (Meetergo renders an iframe in a <div>, not a
      // browser popup — no popup blocker involvement on the happy path).
      api.modal({ link: url });
    } catch {
      // Fallback if the script failed to load or timed out. Note: after the
      // await this no longer counts as a direct user-gesture, so browsers may
      // block this popup. The user will need to allow popups for the site.
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  return { openPopup };
}
